import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  LinearProgress,
} from '@mui/material';
import { ChecklistAccordion } from 'containers/pages/CANguard/components/guardLocation/PatrolDetailModal';
import { usePatrolDetail } from 'containers/pages/CANguard/hooks/dispatch/usePatrolDetail';
import CheckpointEditModal from 'containers/pages/CANguard/components/guardLocation/CheckpointEditModal';

const InlineChecklistDetail = ({ scheduleAssignmentId, allowEdit = false, onRefetch }) => {
  const {
    checklists,
    progress,
    isLoading,
    isError,
    error,
    refetch,
  } = usePatrolDetail({
    scheduleAssignmentId,
    enabled: scheduleAssignmentId != null,
  });

  const [expandedChecklistId, setExpandedChecklistId] = useState(null);
  const [editingCheckpoint, setEditingCheckpoint] = useState(null);

  useEffect(() => {
    if (checklists.length > 0 && expandedChecklistId === null) {
      setExpandedChecklistId(checklists[0].checklistId);
    }
  }, [checklists]);

  const handleCheckpointSaved = () => {
    refetch();
    if (onRefetch) onRefetch();
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error" sx={{ m: 1 }}>
        {error?.message || 'Failed to load checklist details'}
      </Alert>
    );
  }

  if (checklists.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ pl: 2.5 }}>
        No checklists
      </Typography>
    );
  }

  const progressColor = progress.hasEmergencies ? 'error' : progress.hasWarnings ? 'warning' : 'primary';

  return (
    <Box>
      <Box sx={{ mb: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
          <Typography variant="body2" fontWeight={500}>Progress</Typography>
          <Typography variant="body2" color="text.secondary">
            {progress.completedCheckpoints} / {progress.totalCheckpoints} checkpoints
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress.percentComplete}
          color={progressColor}
          sx={{ height: 8, borderRadius: 4, bgcolor: 'divider' }}
        />
      </Box>

      {checklists.map((checklist) => (
        <ChecklistAccordion
          key={checklist.checklistId}
          checklist={checklist}
          expanded={expandedChecklistId === checklist.checklistId}
          onExpandChange={(isExpanded) => setExpandedChecklistId(isExpanded ? checklist.checklistId : null)}
          allowEdit={allowEdit}
          onEditCheckpoint={(checkpoint) => setEditingCheckpoint(checkpoint)}
        />
      ))}

      {allowEdit && scheduleAssignmentId && (
        <CheckpointEditModal
          open={editingCheckpoint !== null}
          onClose={() => setEditingCheckpoint(null)}
          checkpoint={editingCheckpoint}
          scheduleAssignmentId={scheduleAssignmentId}
          onSaved={handleCheckpointSaved}
        />
      )}
    </Box>
  );
};

export default InlineChecklistDetail;
